import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Check, Sparkles, Calculator, ArrowRight, Repeat, Package, Award, Send, FileCheck, TrendingUp, PackageCheck, Handshake, Home, ShieldCheck, Warehouse, Boxes, ScanLine, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/* =====================================================================
   1) SETUP PACKAGE TIERS
===================================================================== */
const PACKAGE_IDS = ["incorporate", "setup", "turnkey"];

function SetupPackages() {
  const { t } = useTranslation();
  const [offer, setOffer] = useState(null);
  useEffect(() => {
    api.get("/platform/turnkey-offer").then((r) => setOffer(r.data)).catch(() => {});
  }, []);
  return (
    <section id="packages" className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="setup-packages">
      <div className="max-w-3xl">
        <span
          data-testid="packages-audience-badge"
          className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3.5 py-1.5 text-[11px] font-heading font-black uppercase tracking-widest text-primary mb-5"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-primary" />
          {t("mkt.packages.audienceBadge")}
        </span>
        <div className="overline text-primary mb-4">{t("mkt.packages.eyebrow")}</div>
        <h2 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight text-secondary leading-tight">
          {t("mkt.packages.h")}
        </h2>
        <p className="mt-4 text-lg text-muted-foreground leading-relaxed">{t("mkt.packages.sub")}</p>
      </div>
      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
        {PACKAGE_IDS.map((id) => {
          const highlight = id === "turnkey";
          const features = t(`mkt.packages.${id}.f`, { returnObjects: true });
          return (
            <div
              key={id}
              data-testid={`pkg-${id}`}
              className={`rounded-lg border p-8 card-lift flex flex-col ${
                highlight
                  ? "bg-secondary text-secondary-foreground border-primary/50 relative"
                  : "bg-white border-border text-secondary"
              }`}
            >
              {highlight && (
                <span className="absolute -top-3 left-6 bg-primary text-primary-foreground text-[10px] font-heading font-black tracking-widest px-3 py-1 rounded-full">
                  {t("mkt.packages.recommended")}
                </span>
              )}
              <div className={`text-xs uppercase tracking-widest font-semibold ${highlight ? "text-primary" : "text-muted-foreground"}`}>
                {t(`mkt.packages.${id}.tag`)}
              </div>
              <div className="mt-4 font-heading text-3xl font-black tracking-tight">{t(`mkt.packages.${id}.name`)}</div>
              <div className="mt-2 font-heading text-5xl font-black text-primary">{t(`mkt.packages.${id}.price`)}</div>
              <p className={`mt-3 text-sm ${highlight ? "text-secondary-foreground/70" : "text-muted-foreground"}`}>{t(`mkt.packages.${id}.for`)}</p>
              {id === "turnkey" && (
                <div
                  data-testid="pkg-turnkey-offer"
                  className={`mt-5 rounded-md border p-4 ${
                    offer && !offer.active
                      ? "border-white/10 bg-white/5 opacity-70"
                      : "border-primary/50 bg-primary/10"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-primary shrink-0" />
                      <span className="overline text-primary text-[10px]">{t("mkt.packages.turnkey.offerTag")}</span>
                    </div>
                    {offer && (
                      <span
                        data-testid="pkg-turnkey-slots"
                        className={`text-[10px] font-heading font-black tracking-widest px-2 py-1 rounded-full ${
                          offer.active ? "bg-primary text-primary-foreground" : "bg-white/10 text-secondary-foreground/60"
                        }`}
                      >
                        {offer.active
                          ? t("mkt.packages.turnkey.slotsLeft", { remaining: offer.remaining, cap: offer.cap })
                          : t("mkt.packages.turnkey.soldOut")}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-secondary-foreground leading-relaxed">
                    {t("mkt.packages.turnkey.offer")}
                  </p>
                </div>
              )}
              <ul className={`mt-6 space-y-2.5 text-sm flex-1 ${highlight ? "text-secondary-foreground/90" : "text-secondary"}`}>
                {Array.isArray(features) &&
                  features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
                      <span>{f}</span>
                    </li>
                  ))}
              </ul>
              <a href="/contact" className="mt-8">
                <Button data-testid={`pkg-cta-${id}`} className="w-full" variant={highlight ? "default" : "outline"}>
                  {t("mkt.packages.cta")} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>
          );
        })}
      </div>
      <p className={`mt-8 text-xs leading-relaxed text-muted-foreground max-w-2xl`} data-testid="pkg-gov-fee">
        {t("mkt.packages.govFee")}
      </p>
      <a href="/packages/details" data-testid="pkg-see-details" className="inline-block mt-4 text-sm font-semibold text-primary hover:underline">
        {t("mkt.packages.seeDetails")}
      </a>
    </section>
  );
}

/* =====================================================================
   1b) ADD-ONS & RECURRING FEES
===================================================================== */
const ADDON_KEYS = [
  { key: "commitment", icon: Handshake },
  { key: "nomineeOy", icon: ShieldCheck },
  { key: "residencyOy", icon: Home },
  { key: "warehouse", icon: Repeat },
  { key: "commission", icon: TrendingUp },
  { key: "sku", icon: Package },
  { key: "trademark", icon: Award },
  { key: "sample", icon: Send },
  { key: "licence", icon: FileCheck },
];

function AddOns() {
  const { t } = useTranslation();
  return (
    <section id="addons" className="bg-muted/40 py-24" data-testid="addons-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <div className="overline text-primary mb-4">{t("mkt.addons.eyebrow")}</div>
          <h2 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight text-secondary leading-tight">
            {t("mkt.addons.h")}
          </h2>
          <p className="mt-4 text-lg text-muted-foreground leading-relaxed">{t("mkt.addons.sub")}</p>
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {ADDON_KEYS.map(({ key, icon: Icon }) => (
            <div
              key={key}
              data-testid={`addon-${key}`}
              className="bg-white border border-border rounded-lg p-7 card-lift flex flex-col"
            >
              <Icon className="w-6 h-6 text-primary mb-5" />
              <div className="flex items-baseline gap-2">
                <div className="font-heading text-3xl font-black text-secondary">{t(`mkt.addons.${key}.price`)}</div>
                <div className="text-xs text-muted-foreground font-medium">{t(`mkt.addons.${key}.unit`)}</div>
              </div>
              <h3 className="mt-4 font-heading text-lg font-bold tracking-tight text-secondary">{t(`mkt.addons.${key}.t`)}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t(`mkt.addons.${key}.d`)}</p>
              {key === "residencyOy" && (
                <a
                  href="/services/visa"
                  data-testid="addon-oy-visa-link"
                  className="mt-4 inline-block text-sm font-semibold text-primary hover:underline"
                >
                  {t("mkt.addons.residencyOy.link")}
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =====================================================================
   2) COMPLIANCE CALCULATOR
===================================================================== */
// Category → { licences, weeks } — technical terms kept in English (industry standard).
const CATEGORY_RULES = {
  "Food & beverage": {
    licences: [
      "EU novel food notification (if applicable)",
      "HACCP certification review",
      "EU food labelling & allergen compliance",
      "Finnish Food Authority (Ruokavirasto) product registration",
    ],
    weeks: "8-14",
  },
  "Coffee & tea": {
    licences: ["HACCP certification review", "EU organic certification (if organic)", "EU food labelling", "Health claim review"],
    weeks: "6-10",
  },
  "Cosmetics & personal care": {
    licences: ["EU Cosmetic Product Notification (CPNP)", "Product Information File (PIF)", "EU Responsible Person appointment", "Safety Assessment (CPSR)"],
    weeks: "10-16",
  },
  "Textiles & apparel": {
    licences: ["REACH substance compliance", "EU textile labelling", "OEKO-TEX review (if claimed)", "Rules-of-origin documentation"],
    weeks: "4-8",
  },
  "Furniture & home": {
    licences: ["CE marking (if applicable)", "Flammability compliance (EN 1021)", "FSC certification review (if wood)", "Formaldehyde emission testing"],
    weeks: "6-12",
  },
  Handicrafts: {
    licences: ["REACH review", "CITES check (for natural materials)", "EU customs classification", "Origin declaration"],
    weeks: "3-6",
  },
  Seafood: {
    licences: ["EU approved establishment number", "Catch certificate", "HACCP + traceability", "Border control post notification"],
    weeks: "10-18",
  },
  Electronics: {
    licences: ["CE marking (LVD, EMC, RoHS)", "WEEE registration", "Battery directive (if applicable)", "Radio Equipment Directive (if wireless)"],
    weeks: "10-20",
  },
  "Agriculture (raw)": {
    licences: ["EU phytosanitary certificate", "Import permit", "Pesticide MRL testing", "Origin declaration"],
    weeks: "6-12",
  },
};
const CATEGORIES = Object.keys(CATEGORY_RULES);

function ComplianceCalculator() {
  const { t } = useTranslation();
  const [cat, setCat] = useState("");
  const [contact, setContact] = useState({ name: "", email: "", company: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const rules = useMemo(() => (cat ? CATEGORY_RULES[cat] : null), [cat]);

  const captureLead = async () => {
    if (!contact.email || !cat || !rules) return;
    setBusy(true);
    try {
      await api.post("/leads/compliance", {
        name: contact.name || undefined,
        email: contact.email,
        company: contact.company || undefined,
        category: cat,
        licences: rules.licences,
        weeks: rules.weeks,
      });
      setSent(true);
      toast.success(t("mkt.cc.toastSent"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="compliance-calculator" className="bg-secondary text-secondary-foreground py-24" data-testid="compliance-calculator">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
          <div className="lg:col-span-2">
            <div className="overline text-primary mb-4">{t("mkt.cc.eyebrow")}</div>
            <h2 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight leading-tight">{t("mkt.cc.h")}</h2>
            <p className="mt-4 text-secondary-foreground/70 leading-relaxed max-w-md">{t("mkt.cc.sub")}</p>
            <div className="mt-8 inline-flex items-center gap-3 text-secondary-foreground/60 text-sm">
              <Calculator className="w-5 h-5 text-primary" />
              <span>{t("mkt.cc.helper")}</span>
            </div>
          </div>
          <div className="lg:col-span-3 bg-white text-secondary rounded-lg p-8">
            <label className="block text-sm font-semibold text-secondary mb-2">{t("mkt.cc.categoryLabel")}</label>
            <select
              data-testid="cc-category"
              value={cat}
              onChange={(e) => {
                setCat(e.target.value);
                setSent(false);
              }}
              className="w-full h-12 px-4 rounded-md border border-input bg-background text-sm"
            >
              <option value="">{t("mkt.cc.choose")}</option>
              {CATEGORIES.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>

            {rules && (
              <div className="mt-6 p-5 rounded-md bg-primary/5 border border-primary/30" data-testid="cc-result">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="overline text-primary">{t("mkt.cc.typicalLicences")}</div>
                    <div className="mt-1 font-heading text-lg font-bold text-secondary">{cat}</div>
                  </div>
                  <Badge className="bg-primary text-primary-foreground">
                    {rules.weeks} {t("mkt.cc.weeksSuffix")}
                  </Badge>
                </div>
                {/* Email gate — teaser (2 items) before submit, full list after */}
                <ul className="mt-4 space-y-2 text-sm text-secondary" data-testid="cc-licences">
                  {(sent ? rules.licences : rules.licences.slice(0, 2)).map((l) => (
                    <li key={l} className="flex items-start gap-2">
                      <Sparkles className="w-3.5 h-3.5 mt-1 text-primary shrink-0" />
                      <span>{l}</span>
                    </li>
                  ))}
                  {!sent && rules.licences.length > 2 && (
                    <li className="flex items-start gap-2 text-muted-foreground italic" data-testid="cc-gated-preview">
                      <Sparkles className="w-3.5 h-3.5 mt-1 opacity-40 shrink-0" />
                      <span>{t("mkt.cc.gatedMore", { count: rules.licences.length - 2 })}</span>
                    </li>
                  )}
                </ul>
                <div className="mt-6 border-t border-primary/20 pt-5">
                  {sent ? (
                    <div className="text-sm text-secondary" data-testid="cc-sent">
                      {t("mkt.cc.sent")}
                    </div>
                  ) : (
                    <>
                      <div className="mb-3 text-xs font-heading font-black uppercase tracking-widest text-primary" data-testid="cc-gate-heading">
                        {t("mkt.cc.gateHeading")}
                      </div>
                      <p className="mb-4 text-xs text-muted-foreground leading-relaxed" data-testid="cc-gate-sub">
                        {t("mkt.cc.gateSub")}
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        <input
                          data-testid="cc-name"
                          className="h-10 px-3 rounded-md border border-input bg-background text-sm"
                          placeholder={t("mkt.cc.namePh")}
                          value={contact.name}
                          onChange={(e) => setContact({ ...contact, name: e.target.value })}
                        />
                        <input
                          data-testid="cc-email"
                          type="email"
                          className="h-10 px-3 rounded-md border border-input bg-background text-sm"
                          placeholder={t("mkt.cc.emailPh")}
                          value={contact.email}
                          onChange={(e) => setContact({ ...contact, email: e.target.value })}
                        />
                        <input
                          data-testid="cc-company"
                          className="h-10 px-3 rounded-md border border-input bg-background text-sm"
                          placeholder={t("mkt.cc.companyPh")}
                          value={contact.company}
                          onChange={(e) => setContact({ ...contact, company: e.target.value })}
                        />
                        <Button data-testid="cc-submit" className="md:col-span-3" disabled={busy || !contact.email} onClick={captureLead}>
                          {busy ? t("mkt.cc.sending") : t("mkt.cc.unlockReport")}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* =====================================================================
   3) WAREHOUSE PHOTO REEL
===================================================================== */
/* =====================================================================
   3) HELSINKI FULFILMENT — text-first block, no stock photos.
   Rebuilt per audit: partner-operated warehouse, no false ownership claims.
===================================================================== */
function WarehouseReel() {
  const { t } = useTranslation();
  const capabilities = [
    { Icon: Warehouse, k: "capReceiving" },
    { Icon: ScanLine,  k: "capScan" },
    { Icon: Boxes,     k: "capStorage" },
    { Icon: Truck,     k: "capDispatch" },
  ];
  return (
    <section className="bg-white py-24" data-testid="warehouse-reel">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-12">
          <div className="max-w-2xl">
            <div className="overline text-primary mb-4">{t("mkt.warehouse.eyebrow")}</div>
            <h2 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight text-secondary leading-tight">
              {t("mkt.warehouse.h")}
            </h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">{t("mkt.warehouse.sub")}</p>
          </div>
          <div className="flex items-center gap-6 text-sm">
            {[
              { k: "8,400 m²", v: t("mkt.warehouse.footprint") },
              { k: "72h",      v: t("mkt.warehouse.delivery") },
              { k: "24/7",     v: t("mkt.warehouse.monitoring") },
            ].map((s) => (
              <div key={s.v} className="border-l border-border pl-4">
                <div className="font-heading text-2xl font-black text-secondary">{s.k}</div>
                <div className="text-xs text-muted-foreground">{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Capabilities — icon grid instead of stock photos */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="warehouse-capabilities">
          {capabilities.map(({ Icon, k }, i) => (
            <div
              key={k}
              data-testid={`warehouse-cap-${i}`}
              className="group rounded-lg border border-border bg-[#faf8f4] p-6 hover:border-primary/40 hover:shadow-md transition-all duration-300"
            >
              <div className="w-11 h-11 rounded-md bg-primary/10 text-primary grid place-items-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                <Icon className="w-5 h-5" />
              </div>
              <div className="mt-5 font-heading font-black text-secondary tracking-tight text-lg leading-tight">
                {t(`mkt.warehouse.${k}.t`)}
              </div>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                {t(`mkt.warehouse.${k}.d`)}
              </p>
            </div>
          ))}
        </div>

        <p className="mt-8 text-xs text-muted-foreground max-w-3xl leading-relaxed">
          {t("mkt.warehouse.footnote")}
        </p>
      </div>
    </section>
  );
}

/* =====================================================================
   4) SAMPLE PROGRAM — €350 buyer sample bundle → Stripe checkout
===================================================================== */
const SP_HERO_IMG =
  "https://images.unsplash.com/photo-1568992687947-868a62a9f521?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80";
const EU_COUNTRIES = [
  "Finland", "Sweden", "Denmark", "Norway", "Germany", "France", "Netherlands",
  "Belgium", "Spain", "Italy", "Poland", "Estonia", "Austria", "Ireland", "Portugal", "Other EU",
];

function SampleProgram() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [f, setF] = useState({
    name: "", email: "", company: "", phone: "",
    quantity: 1, product_description: "", shipping_country: "Finland", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const total = 350 * Number(f.quantity || 1);

  // Handle Stripe success/cancel redirects
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paid = params.get("sample_paid");
    const cancelled = params.get("sample_cancelled");
    if (paid) {
      // Poll status once, then celebrate
      api.get(`/samples/program-request/status/${paid}`).catch(() => {});
      toast.success(t("mkt.sp.paidTitle"));
      // Scroll and clean URL
      navigate("/#sample-program", { replace: true });
    } else if (cancelled) {
      toast(t("mkt.sp.cancelTitle"), { description: t("mkt.sp.cancelBody") });
      navigate("/#sample-program", { replace: true });
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.email || !f.name || !f.product_description) return;
    setBusy(true);
    try {
      const { data } = await api.post("/samples/program-request", {
        name: f.name, email: f.email, company: f.company || undefined,
        phone: f.phone || undefined,
        quantity: Number(f.quantity), product_description: f.product_description,
        shipping_country: f.shipping_country, notes: f.notes || undefined,
      });
      if (data?.checkout_url) {
        toast.success(t("mkt.sp.loading"));
        window.location.assign(data.checkout_url);
      }
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <section id="sample-program" className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="sample-program">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
        {/* LEFT — pitch */}
        <div className="lg:col-span-2">
          <div className="overline text-primary mb-4">{t("mkt.sp.eyebrow")}</div>
          <h2 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight text-secondary leading-tight">
            {t("mkt.sp.h")}
          </h2>
          <p className="mt-4 text-lg text-muted-foreground leading-relaxed">{t("mkt.sp.sub")}</p>

          <div className="mt-8 rounded-lg overflow-hidden card-lift">
            <img src={SP_HERO_IMG} alt="Sample shipment" className="w-full h-56 object-cover" />
          </div>

          <div className="mt-8 flex items-baseline gap-3">
            <div className="font-heading text-6xl font-black text-primary">{t("mkt.sp.priceTag")}</div>
            <div className="text-sm text-muted-foreground">{t("mkt.sp.unit")}</div>
          </div>

          <ul className="mt-6 space-y-2.5 text-sm text-secondary">
            {["b1", "b2", "b3", "b4"].map((k) => (
              <li key={k} className="flex items-start gap-2">
                <PackageCheck className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
                <span>{t(`mkt.sp.${k}`)}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* RIGHT — form */}
        <form onSubmit={submit} className="lg:col-span-3 bg-secondary text-secondary-foreground rounded-lg p-8" data-testid="sample-program-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-widest text-secondary-foreground/70">{t("mkt.sp.namePh")}</span>
              <input
                required data-testid="sp-name"
                className="mt-1.5 w-full h-11 px-3 rounded-md bg-white text-secondary border border-white/20 text-sm"
                value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-widest text-secondary-foreground/70">{t("mkt.sp.emailPh")}</span>
              <input
                required type="email" data-testid="sp-email"
                className="mt-1.5 w-full h-11 px-3 rounded-md bg-white text-secondary border border-white/20 text-sm"
                value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-widest text-secondary-foreground/70">{t("mkt.sp.companyPh")}</span>
              <input
                data-testid="sp-company"
                className="mt-1.5 w-full h-11 px-3 rounded-md bg-white text-secondary border border-white/20 text-sm"
                value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-widest text-secondary-foreground/70">{t("mkt.sp.phonePh")}</span>
              <input
                data-testid="sp-phone"
                className="mt-1.5 w-full h-11 px-3 rounded-md bg-white text-secondary border border-white/20 text-sm"
                value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })}
              />
            </label>

            <label className="block md:col-span-2">
              <span className="text-xs font-semibold uppercase tracking-widest text-secondary-foreground/70">{t("mkt.sp.productLabel")}</span>
              <input
                required data-testid="sp-product"
                className="mt-1.5 w-full h-11 px-3 rounded-md bg-white text-secondary border border-white/20 text-sm"
                placeholder={t("mkt.sp.productPh")}
                value={f.product_description} onChange={(e) => setF({ ...f, product_description: e.target.value })}
              />
            </label>

            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-widest text-secondary-foreground/70">{t("mkt.sp.quantityLabel")}</span>
              <input
                type="number" min={1} max={20} data-testid="sp-quantity"
                className="mt-1.5 w-full h-11 px-3 rounded-md bg-white text-secondary border border-white/20 text-sm"
                value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-widest text-secondary-foreground/70">{t("mkt.sp.countryLabel")}</span>
              <select
                data-testid="sp-country"
                className="mt-1.5 w-full h-11 px-3 rounded-md bg-white text-secondary border border-white/20 text-sm"
                value={f.shipping_country} onChange={(e) => setF({ ...f, shipping_country: e.target.value })}
              >
                {EU_COUNTRIES.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </label>

            <label className="block md:col-span-2">
              <span className="text-xs font-semibold uppercase tracking-widest text-secondary-foreground/70">{t("mkt.sp.notesLabel")}</span>
              <textarea
                data-testid="sp-notes" rows={3}
                className="mt-1.5 w-full px-3 py-2 rounded-md bg-white text-secondary border border-white/20 text-sm"
                placeholder={t("mkt.sp.notesPh")}
                value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}
              />
            </label>
          </div>

          <div className="mt-6 flex items-center justify-end border-t border-white/10 pt-6">
            <Button
              type="submit"
              disabled={busy}
              data-testid="sp-submit"
              size="lg"
              className="h-12 px-8"
            >
              {busy ? t("mkt.sp.loading") : t("mkt.sp.cta")}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
}

export { SetupPackages, AddOns, ComplianceCalculator, WarehouseReel, SampleProgram, BuyerPricing };

/* =====================================================================
   6) BUYER PRICING — for EU importers
===================================================================== */
function BuyerPricing() {
  const { t } = useTranslation();
  const items = t("mkt.buyerPricing.items", { returnObjects: true });
  return (
    <section
      id="buyer-pricing"
      data-testid="buyer-pricing"
      className="max-w-7xl mx-auto px-6 lg:px-10 py-24 border-t border-border/70"
    >
      <div className="max-w-3xl">
        <span
          data-testid="buyer-pricing-badge"
          className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3.5 py-1.5 text-[11px] font-heading font-black uppercase tracking-widest text-primary mb-5"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-primary" />
          {t("mkt.buyerPricing.badge")}
        </span>
        <div className="overline text-primary mb-4">{t("mkt.buyerPricing.eyebrow")}</div>
        <h2 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight text-secondary leading-tight">
          {t("mkt.buyerPricing.h")}
        </h2>
        <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
          {t("mkt.buyerPricing.sub")}
        </p>
      </div>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
        {Array.isArray(items) && items.map((it, i) => (
          <div
            key={i}
            data-testid={`buyer-pricing-item-${i}`}
            className={`rounded-lg border p-8 card-lift flex flex-col ${
              it.highlight
                ? "bg-secondary text-secondary-foreground border-primary/50 relative"
                : "bg-white border-border text-secondary"
            }`}
          >
            {it.highlight && (
              <span className="absolute -top-3 left-6 bg-primary text-primary-foreground text-[10px] font-heading font-black tracking-widest px-3 py-1 rounded-full">
                {it.badge}
              </span>
            )}
            <div className={`text-xs uppercase tracking-widest font-semibold ${it.highlight ? "text-primary" : "text-muted-foreground"}`}>
              {it.tag}
            </div>
            <div className="mt-4 font-heading text-3xl font-black tracking-tight">{it.name}</div>
            <div className="mt-2 font-heading text-4xl font-black text-primary">{it.price}</div>
            <p className={`mt-3 text-sm ${it.highlight ? "text-secondary-foreground/70" : "text-muted-foreground"}`}>{it.for}</p>
            <ul className="mt-6 space-y-2 text-sm flex-1">
              {(it.f || []).map((f, fi) => (
                <li key={fi} className="flex items-start gap-2">
                  <Check className={`w-4 h-4 mt-0.5 shrink-0 text-primary`} />
                  <span className={it.highlight ? "text-secondary-foreground/90" : "text-secondary"}>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <p className="mt-6 text-xs text-muted-foreground max-w-3xl">
        {t("mkt.buyerPricing.footnote")}
      </p>
    </section>
  );
}

